import { useCallback, useEffect, useRef, useState } from "react";
// @ts-expect-error pure js
import { handleAPIError } from "../../../utils";

interface UsePdfPreviewCacheArgs<TPayload> {
  activeKey: string | null;
  activePayload: TPayload | null;
  fetcher: (payload: TPayload) => Promise<string>;
  enabled: boolean;
}

export function usePdfPreviewCache<TPayload>({
  activeKey,
  activePayload,
  fetcher,
  enabled,
}: UsePdfPreviewCacheArgs<TPayload>) {
  const [cache, setCache] = useState<Record<string, string>>({});
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  const inFlightRef = useRef<Set<string>>(new Set());
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchPreview = useCallback(
    async (key: string, payload: TPayload) => {
      if (cacheRef.current[key] !== undefined) return;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      setLoadingKeys((prev) => new Set(prev).add(key));
      setErrors((prev) => {
        if (prev[key] === undefined) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      try {
        const url = await fetcher(payload);
        setCache((prev) => ({ ...prev, [key]: url }));
      } catch (e) {
        setErrors((prev) => ({ ...prev, [key]: handleAPIError(e) }));
      } finally {
        inFlightRef.current.delete(key);
        setLoadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [fetcher],
  );

  useEffect(() => {
    if (activeKey !== null && activePayload !== null && enabled) {
      fetchPreview(activeKey, activePayload);
    }
  }, [activeKey, activePayload, enabled, fetchPreview]);

  useEffect(() => {
    return () => {
      Object.values(cacheRef.current).forEach((url) => {
        if (url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
    };
  }, []);

  return {
    previewUrl: activeKey ? cache[activeKey] : undefined,
    previewLoading: activeKey ? loadingKeys.has(activeKey) : false,
    previewError: activeKey ? errors[activeKey] : undefined,
  };
}
